import type { Rng } from './rng';
import { createRng, defaultRng } from './rng';
import { generateEmail, generateUaePhone } from './primitives';
import { COMPANIES, PEOPLE, type SampleCompany, type SamplePerson } from './uae';

export type PortalRole = 'CustomerAdmin' | 'Accountant' | 'Approver' | 'Viewer';
export type UserStatus = 'active' | 'invited' | 'suspended';

export interface SampleUser {
  userId: string;
  givenName: string;
  familyName: string;
  givenNameAr: string;
  familyNameAr: string;
  email: string;
  phone: string;
  role: PortalRole;
  status: UserStatus;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

const ROLES: PortalRole[] = ['CustomerAdmin', 'Accountant', 'Approver', 'Viewer'];

function pseudoUuid(rng: Rng): string {
  const hex = (n: number) => rng.int(0, 15).toString(16).repeat(n);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}

function isoMinus(days: number, hour = 9): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
}

export function generateSampleUsers(
  count = 22,
  seed = 0xb1adb1,
  company?: SampleCompany,
): SampleUser[] {
  const rng = createRng(seed);
  const co = company ?? rng.pick(COMPANIES);
  const out: SampleUser[] = [];
  // Distribute roles: ~1 admin, ~5 accountants, ~3 approvers, rest viewers.
  const roleQuota: Record<PortalRole, number> = {
    CustomerAdmin: Math.max(1, Math.round(count * 0.08)),
    Accountant: Math.round(count * 0.32),
    Approver: Math.round(count * 0.18),
    Viewer: 0,
  };
  roleQuota.Viewer = Math.max(
    1,
    count - roleQuota.CustomerAdmin - roleQuota.Accountant - roleQuota.Approver,
  );

  let i = 0;
  for (const role of ROLES) {
    for (let r = 0; r < roleQuota[role]; r++, i++) {
      if (i >= count) break;
      const person: SamplePerson = (PEOPLE[i % PEOPLE.length] ?? PEOPLE[0]) as SamplePerson;
      const status: UserStatus =
        r === 0 && role === 'CustomerAdmin'
          ? 'active'
          : rng.chance(0.85)
          ? 'active'
          : rng.chance(0.6)
          ? 'invited'
          : 'suspended';
      out.push({
        userId: pseudoUuid(rng),
        givenName: person.givenName,
        familyName: person.familyName,
        givenNameAr: person.givenNameAr,
        familyNameAr: person.familyNameAr,
        email: generateEmail(person.givenName, person.familyName, co.slug),
        phone: generateUaePhone(rng),
        role,
        status,
        mfaEnabled: status !== 'invited' && rng.chance(0.85),
        lastLoginAt: status === 'active' ? isoMinus(rng.int(0, 21), rng.int(7, 19)) : null,
        createdAt: isoMinus(rng.int(7, 240)),
      });
    }
  }
  return out;
}

export function pickSampleUser(users: SampleUser[], rng: Rng = defaultRng()): SampleUser {
  return rng.pick(users);
}
